import React from 'react';
import { ChangeHistory } from '../services/changeHistoryService';

interface ChangeHistoryTimelineProps {
  history: ChangeHistory[];
}

const ChangeHistoryTimeline: React.FC<ChangeHistoryTimelineProps> = ({ history }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };
    return date.toLocaleString('en-US', options);
  };

  const formatFieldName = (fieldName: string) => {
    // Convert camelCase or dot notation to readable format
    // e.g., "contactEmail" -> "Contact Email"
    // e.g., "address.city" -> "Address > City"
    if (fieldName.includes('.')) {
      return fieldName
        .split('.')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).replace(/([A-Z])/g, ' $1'))
        .join(' > ');
    }
    return fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/([A-Z])/g, ' $1');
  };

  const stripHtmlTags = (html: string): string => {
    // Create a temporary div to parse HTML
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const formatValue = (value: any) => {
    if (value === null || value === undefined) {
      return '(empty)';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    const stringValue = String(value);

    // Check if it looks like HTML content (contains HTML tags)
    if (stringValue.includes('<') && stringValue.includes('>')) {
      // Strip HTML tags and return plain text
      return stripHtmlTags(stringValue);
    }

    return stringValue;
  };

  const renderChangeValue = (change: ChangeHistory) => {
    // For CREATE operations, only show new value
    if (change.changeType === 'CREATE') {
      return (
        <div style={{ marginTop: '8px' }}>
          <div
            style={{
              padding: '8px 12px',
              backgroundColor: '#D1FAE5',
              borderRadius: '6px',
              fontSize: '14px',
              color: '#065F46',
            }}
          >
            <strong>Created:</strong> {formatValue(change.newValue)}
          </div>
        </div>
      );
    }

    // For DELETE operations, only show old value
    if (change.changeType === 'DELETE') {
      return (
        <div style={{ marginTop: '8px' }}>
          <div
            style={{
              padding: '8px 12px',
              backgroundColor: '#FEE2E2',
              borderRadius: '6px',
              fontSize: '14px',
              color: '#991B1B',
            }}
          >
            <strong>Deleted:</strong> {formatValue(change.oldValue)}
          </div>
        </div>
      );
    }

    // For UPDATE operations, show old and new values
    return (
      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div
          style={{
            padding: '8px 12px',
            backgroundColor: '#FEE2E2',
            borderRadius: '6px',
            fontSize: '14px',
            color: '#991B1B',
          }}
        >
          <strong>Old:</strong> {formatValue(change.oldValue)}
        </div>
        <div
          style={{
            padding: '8px 12px',
            backgroundColor: '#D1FAE5',
            borderRadius: '6px',
            fontSize: '14px',
            color: '#065F46',
          }}
        >
          <strong>New:</strong> {formatValue(change.newValue)}
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: 'relative' }}>
      {history.map((change, index) => (
        <div
          key={change._id}
          style={{
            position: 'relative',
            paddingBottom: index < history.length - 1 ? '24px' : '0',
          }}
        >
          {/* Vertical line */}
          {index < history.length - 1 && (
            <div
              style={{
                position: 'absolute',
                left: '20px',
                top: '48px',
                bottom: '0',
                width: '2px',
                backgroundColor: '#E5E7EB',
              }}
            />
          )}

          {/* Change card */}
          <div
            style={{
              backgroundColor: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              padding: '16px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              position: 'relative',
            }}
          >
            {/* Header */}
            <div style={{ marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: '600', color: '#48182F', fontSize: '15px' }}>
                  {change.changedByName}
                </span>
                <span style={{ color: '#6B7280', fontSize: '14px' }}>
                  {change.changeType === 'CREATE' && 'created'}
                  {change.changeType === 'UPDATE' && 'changed'}
                  {change.changeType === 'DELETE' && 'deleted'}
                </span>
                <span style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                  {formatFieldName(change.fieldName)}
                </span>
              </div>
              <div style={{ color: '#9CA3AF', fontSize: '13px', marginTop: '4px' }}>
                {formatDate(change.changedAt)}
              </div>
            </div>

            {/* Value changes */}
            {renderChangeValue(change)}

            {/* Optional reason */}
            {change.changeReason && (
              <div
                style={{
                  marginTop: '12px',
                  padding: '8px 12px',
                  backgroundColor: '#F3F4F6',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#4B5563',
                  fontStyle: 'italic',
                }}
              >
                <strong>Reason:</strong> {change.changeReason}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ChangeHistoryTimeline;
